/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { FormEvent, useEffect, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import Input from "components/generals/Input";
import Typography from "components/generals/Typography";
import Grid from "@mui/material/Grid";
import PageLayout from "dashboard-template/examples/LayoutContainers/PageLayout";
import { useNavigate } from "react-router-dom";
import useHttp from "hooks/http.hook";
import { UserAuthInterface, UserInterface, HeaderEnum } from "medicalsystem-shared";
import { authAction, eventAction, useAppDispatch } from "store/store";
import { RoutesConstants } from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import MDAlert from "dashboard-template/components/MDAlert";
import VerificationModal from "components/ui/VerificationModal";
import { VerificationConstants } from "constants/env.const";
import { useMaterialUIController } from "context/ThemeContext";
import { ExtendedCSSProperties } from "interfaces";

/**
 * User login page with two-factor authentication support.
 * 
 * Features:
 * - Username/password authentication
 * - Password visibility toggle
 * - Remember me functionality
 * - Two-factor authentication (2FA) with verification code
 * - Code resend with 180-second cooldown timer
 * - Comprehensive error handling (404, 403, connection errors)
 * - Form validation before submission
 * - Integration with Redux for auth state management
 * - Automatic navigation to dashboard after successful login
 * - Password recovery link
 * - Responsive layout with background image (desktop only)
 * 
 * Authentication Flow:
 * 1. User enters credentials and submits form
 * 2. If VERIFICATION_BASIC_AUTH: Direct login to dashboard
 * 3. If VERIFICATION_TWO_FACTOR: Send verification code to user
 * 4. User enters 6-digit code in modal
 * 5. On successful verification: Fetch user data and store in Redux
 * 6. Navigate to dashboard
 * 
 * @returns Login page component
 */
function Login(): JSX.Element {
  const { t } = useTranslation();
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [isLoading, httpError, sendRequest] = useHttp();
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const VALIDATION = process.env.REACT_APP_VERIFICATION;

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  const toggleRememberMe = () => setRememberMe((prev) => !prev);
  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const fetchUserById = (id: string, onSuccess: (user: UserInterface) => void) => {
    sendRequest(RoutesConstants.USER_ID, onSuccess, "GET", null, [
      { header: HeaderEnum.USER_ID, value: id },
    ]);
  };

  const onGetUserHandler = (userResponse: UserInterface) => {
    if (userResponse && typeof userResponse === "object" && "id" in userResponse) {
      fetchUserById(userResponse.id, (userData) => {
        dispatch(authAction.storeUser(userData));
        setOpenModal(false);
        navigate(RoutesConstants.DASHBOARD);
      });
    } else {
      setCodeError(true);
    }
  };

  const handleTwoStepVerification = (user: UserInterface, password: string) => {
    sendRequest(RoutesConstants.USER_AUTH_LOGIN, null, "GET", null, [
      { header: "username", value: user.username },
      { header: "password", value: password },
      { header: "validate", value: "true" },
    ]);
    setCodeError(false);
    setOpenModal(true);
    setResendTimer(180);
  };

  const successUserHandler = (user: UserInterface, password: string) => {
    if (VALIDATION === VerificationConstants.VERIFICATION_BASIC_AUTH) {
      dispatch(authAction.storeUser(user));
      navigate(RoutesConstants.DASHBOARD);
    } else if (VALIDATION === VerificationConstants.VERIFICATION_TWO_FACTOR) {
      handleTwoStepVerification(user, password);
    }
  };

  const successResultHandler = (auth: UserAuthInterface, password: string) => {
    if (!auth || !auth.id) {
      setFormError(t("login.serverResponseError"));
      return;
    }

    dispatch(authAction.storeAuth(auth));
    fetchUserById(auth.id, (user) => {
      if (user) {
        successUserHandler(user, password);
      } else {
        setFormError(t("login.userInfoLoadFailed"));
      }
    });
  };

  const submitHandle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(eventAction.resetAllMessages());
    setFormError(null);

    if (!username.trim() || !password.trim()) {
      setFormError(t("login.enterCredentials"));
      return;
    }

    try {
      await new Promise((resolve, reject) => {
        sendRequest(
          RoutesConstants.USER_AUTH_LOGIN,
          (auth: UserAuthInterface) => {
            successResultHandler(auth, password);
            resolve(auth);
          },
          "GET",
          null,
          [
            { header: "username", value: username },
            { header: "password", value: password },
          ]
        ).catch(reject);
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setFormError(t("login.userNotFound"));
      } else if (error?.response?.status === 403) {
        setFormError(t("login.incorrectPassword"));
      } else {
        setFormError(t("login.connectionError"));
      }
    }
  };

  const verifyCodeAndCloseModal = () => {
    const inputs = codeContainerRef.current?.querySelectorAll("input");
    const code = Array.from(inputs || [])
      .map((input) => (input as HTMLInputElement).value)
      .join("");
    setCodeError(false);
    sendRequest(RoutesConstants.USER_AUTH_VALIDATE, onGetUserHandler, "GET", null, [
      { header: "username", value: username },
      { header: "password", value: password },
      { header: "code", value: code },
    ]);
  };

  const resendCode = () => {
    setCodeError(false);
    sendRequest(RoutesConstants.USER_AUTH_CODE, null, "GET", null, [
      { header: "username", value: username },
      { header: "password", value: password },
    ]);
  };

  return (
    <PageLayout background={darkMode ? "light" : "white"}>
      <form onSubmit={submitHandle}>
        <Grid container>
          <Grid item xs={11} sm={8} md={6} lg={4} xl={3} sx={{ mx: "auto" }}>
            <Box display="flex" flexDirection="column" justifyContent="center" height="100vh">
              <Box py={3} px={3} textAlign="center">
                <Box mb={1} textAlign="center">
                  <Typography variant="h4" fontWeight="bold">
                    {t("login.tittle")}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text">
                  {t("login.subtittle")}
                </Typography>
              </Box>
              <Box p={3}>
                {httpError && (
                  <Box mb={2}>
                    <MDAlert color="error" dismissible={true} sx={{ mb: 1 }}>
                      {httpError}
                    </MDAlert>
                  </Box>
                )}

                {formError && (
                  <Box mb={2}>
                    <MDAlert color="error" dismissible={true}>
                      {formError}
                    </MDAlert>
                  </Box>
                )}

                <Box>
                  <Box mb={2}>
                    <Input
                      type="text"
                      name="name"
                      label={t("login.username")}
                      fullWidth
                      id="cy-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </Box>
                  <Box mb={2}>
                    <Input
                      type="text"
                      name="password"
                      label={t("login.password")}
                      fullWidth
                      id="cy-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="off"
                      inputProps={{
                        'data-testid': 'password-input',
                        style: {
                          '-webkit-text-security': showPassword ? 'none' : 'disc',
                        } as ExtendedCSSProperties,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={toggleShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              onMouseUp={handleMouseDownPassword}
                              edge="end"
                              tabIndex={-1}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" ml={-1}>
                    <Switch checked={rememberMe} onChange={toggleRememberMe} />
                    <Typography
                      variant="button"
                      fontWeight="regular"
                      color="text"
                      onClick={toggleRememberMe}
                      sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                    >
                      {t("login.rememberMe")}
                    </Typography>
                  </Box>
                  <Box mt={4} mb={1}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      size="large"
                      fullWidth
                      id="cy-signin"
                      disabled={isLoading}
                    >
                      {t("login.singIn")}
                    </Button>
                  </Box>
                  <Box mt={4} mb={1}>
                    <Button
                      type="button"
                      onClick={() => navigate(RoutesConstants.PASSWORD_RECOVERY)}
                      variant="text"
                      color={darkMode ? "white" : "info"}
                      size="large"
                      fullWidth
                      id="cy-signin"
                      disabled={isLoading}
                    >
                      {t("login.forgotPassword")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} lg={8}>
            <Box
              display={{ xs: "none", lg: "flex" }}
              width="calc(100% - 2rem)"
              height="calc(100vh - 2rem)"
              borderRadius="xxl"
              ml={2}
              mt={2}
              sx={{
                backgroundImage:
                  "url(https://smartclinix.net/wp-content/uploads/2021/10/emr-implementation-in-hospitals-1.jpg)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            />
          </Grid>
        </Grid>
      </form>

      <VerificationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onVerify={(code) => {
          setCodeError(false);
          sendRequest(RoutesConstants.USER_AUTH_VALIDATE, onGetUserHandler, "GET", null, [
            { header: "username", value: username },
            { header: "password", value: password },
            { header: "code", value: code },
          ]);
        }}
        onResend={() => {
          resendCode();
          setResendTimer(180);
        }}
        codeError={codeError}
        resendTimer={resendTimer}
      />
    </PageLayout>
  );
}

export default Login;
